// #351, brief §5.4 and §15 rows 29 and 33: the one generator of a program
// model. The athlete's choices (level, focus, minutes, sessions a week,
// blocks, the exercise of each row) pick the rows and their rules; each rule writes its
// cells (rowsForRule), and every block after the first is flow 9
// (addNextBlock). Keep free of React and any UI import.
import { rirToRpe, rpeToRir, type EffortScale } from "./effortScale.js";
import type { Locale } from "./locale.js";
import { blankSlot, type Draft, type SlotDraft } from "./programEdit.js";
import {
  modelDayName,
  modelDaysFor,
  modelRowEntry,
  type ModelDay,
  type ModelLevel,
  type ModelRow,
  type ProgramModel,
  SESSION_MINUTES,
  type SessionMinutes,
} from "./programModel.js";
import { effortOn, harderSign, type ProgressionRule, type Sequence } from "./programProgression.js";
import { rowsForRule } from "./programProgressionApply.js";
import { addNextBlock } from "./programProgressionRounds.js";
import { draftDaySeconds } from "./programModelFit.js";
import type { Exercise } from "./schemas/exercise.js";
import { NEW_PROGRAM_STATUS } from "./schemas/program.js";
import type { ProgramFocus, RoundType } from "./schemas/programV3.js";
import { uuidv4 } from "./uuid.js";

export type ModelChoices = {
  name: string;
  level: ModelLevel;
  /** #356: absent, every row as byLevel writes it. */
  focus?: ProgramFocus;
  minutes: SessionMinutes;
  /** One of the model's values: the caller offers only those. */
  sessionsPerWeek: number;
  /** How many times the round pattern runs; each block after the first
   *  starts again a step higher. */
  blocks: number;
  /** A row's exercise, by row id. Only the row's own alternatives count. */
  exercisePicks?: Record<string, string>;
  effortScale: EffortScale;
  /** The language the program is written in (Draft.editLocale). */
  editLocale?: Locale | null;
  /** The catalog name of a slug, stored with the slot. */
  exerciseName?: (slug: string) => string | undefined;
  /** #356: the model's id; with it the program keeps its origin. */
  modelId?: string;
  /** #413: the catalog by slug, for how long a day lasts (an exercise's own
   *  rest, a timed set). Absent: the fallbacks. */
  exercises?: ReadonlyMap<string, Exercise>;
  /** #492 F3: the row ids the athlete keeps when the time runs short. The fit
   *  never cuts one (no set, no row) and takes the cut from the next row it
   *  may cut. Absent: the fit as it was. */
  pinned?: ReadonlySet<string>;
};

type Num = (x: number) => number;

/** `seq` with its values through `v` and its steps through `d`. */
const mapSeq = (seq: Sequence, v: Num, d: Num): Sequence => {
  switch (seq.kind) {
    case "constant":
      return { kind: "constant", value: v(seq.value) };
    case "linear":
      return { kind: "linear", start: v(seq.start), step: d(seq.step), ...(seq.cap !== undefined ? { cap: v(seq.cap) } : {}) };
    case "ramp":
      return { ...seq, from: v(seq.from), to: v(seq.to) };
    case "cycle":
      return { kind: "cycle", values: seq.values.map(v) };
    case "blocks":
      return { kind: "blocks", blocks: seq.blocks.map((b) => ({ ...b, seq: mapSeq(b.seq, v, d) })) };
  }
};

/** `rule` on `axis`: its values through `v`, its steps, offsets and deltas
 *  through `d`. A deload factor is a ratio and stays. */
export const mapRuleValues = (rule: ProgressionRule, axis: ProgressionRule["axis"], v: Num, d: Num): ProgressionRule => {
  const { perSet, threshold, test, deload } = rule;
  return {
    ...rule,
    axis,
    value: rule.value && mapSeq(rule.value, v, d),
    ...(perSet ? { perSet: { ...perSet, ...(perSet.value ? { value: perSet.value.map(d) } : {}) } } : {}),
    ...(threshold ? { threshold: { ...threshold, loadStart: v(threshold.loadStart), loadStep: d(threshold.loadStep) } } : {}),
    ...(test ? { test: { ...test, ...(test.delta === undefined ? {} : { delta: d(test.delta) }) } } : {}),
    ...(deload ? { deload: { ...deload, ...(deload.delta === undefined ? {} : { delta: d(deload.delta) }) } } : {}),
  };
};

/** A step on `axis` written on the other effort scale: RIR turns its sign. */
const effortStep = (axis: ProgressionRule["axis"]): Num => (x) => (x === 0 ? 0 : x * harderSign(axis));

/** #356: a history row's deload effort (DeloadEffect.effort) on `scale`. */
const deloadEffortOn = (rule: ProgressionRule, scale: EffortScale): ProgressionRule => {
  const e = rule.deload?.effort;
  if (!e || e.axis === scale) return rule;
  const value = scale === "rir" ? rpeToRir(e.value) : rirToRpe(e.value);
  return { ...rule, deload: { ...rule.deload, effort: { axis: scale, value } } };
};

/** A model rule on the athlete's effort scale. Models store effort in RPE
 *  (§15 row 29); any other axis is left as it is. */
export const ruleOnScale = (rule: ProgressionRule, scale: EffortScale): ProgressionRule => {
  const r = deloadEffortOn(rule, scale);
  if (r.axis !== "rpe" || scale === "rpe") return r;
  return mapRuleValues(r, scale, (x) => effortOn(x, scale), effortStep(scale));
};

/** ruleOnScale's inverse: a rule in RIR written in RPE, as a model stores it
 *  (#354). Any other axis is left as it is. */
export const ruleToRpe = (rule: ProgressionRule): ProgressionRule => {
  const r = deloadEffortOn(rule, "rpe");
  if (r.axis !== "rir") return r;
  return mapRuleValues(r, "rpe", rirToRpe, effortStep("rir"));
};

/** One set fewer, never below one: the per-set values lose their last entry,
 *  and a threshold's set ceiling comes down with it. */
export const oneSetFewer = (rule: ProgressionRule): ProgressionRule => {
  const sets = Math.max(1, rule.sets - 1);
  if (sets === rule.sets) return rule;
  const { perSet, threshold } = rule;
  return {
    ...rule,
    sets,
    ...(perSet
      ? {
          perSet: {
            ...(perSet.reps ? { reps: perSet.reps.slice(0, sets) } : {}),
            ...(perSet.value ? { value: perSet.value.slice(0, sets) } : {}),
          },
        }
      : {}),
    ...(threshold?.setsMax !== undefined
      ? { threshold: { ...threshold, setsMax: Math.max(sets, threshold.setsMax - 1) } }
      : {}),
  };
};

type ModelAt = NonNullable<ReturnType<typeof modelRowEntry>>;
type ModelEntry = ModelAt["entry"];

/** The row's entry at `level` and `focus`; null when the row is not there
 *  at these choices (no entry for the level, or an optional row under 75
 *  minutes). */
export const modelRowAt = (
  row: ModelRow,
  level: ModelLevel,
  minutes: SessionMinutes,
  focus?: ProgramFocus,
): ModelEntry | null => modelSlotAt(row, level, minutes, focus)?.entry ?? null;

/** An optional row is there only from this many minutes. */
const OPTIONAL_ROW_MIN_MINUTES = 75;

const modelSlotAt = (row: ModelRow, level: ModelLevel, minutes: SessionMinutes, focus?: ProgramFocus): ModelAt | null => {
  const at = modelRowEntry(row, level, focus);
  return !at || (at.weight === "optional" && minutes < OPTIONAL_ROW_MIN_MINUTES) ? null : at;
};

/** The row's exercise: the pick when it is one of the row's own, else the
 *  default (the first). */
export const modelRowExercise = (entry: ModelEntry, pick: string | undefined): string =>
  pick !== undefined && entry.exercises.includes(pick) ? pick : entry.exercises[0]!;

/** The slot of a row there at `at`, with one set fewer when `fewer`. */
const slotOf = (row: ModelRow, at: ModelAt, choices: ModelChoices, types: readonly RoundType[], fewer: boolean): SlotDraft => {
  const { entry } = at;
  const exerciseId = modelRowExercise(entry, choices.exercisePicks?.[row.id]);
  let rule = ruleOnScale(entry.rule, choices.effortScale);
  if (fewer) rule = oneSetFewer(rule);
  const rest = entry.restSec === undefined ? undefined : String(entry.restSec);
  const setRows = rowsForRule(rule, types, types.length);
  return {
    ...blankSlot(types.length),
    exerciseId,
    exerciseName: choices.exerciseName?.(exerciseId),
    rowId: row.id,
    setRows: rest === undefined ? setRows : setRows.map((r) => ({ ...r, cells: r.cells.map((c) => (c.absent ? c : { ...c, restSec: rest })) })),
  };
};

/** The tier's one cut on sets: an accessory at 45 minutes has one fewer. */
const tierSetFewer = (at: ModelAt, minutes: SessionMinutes): boolean => at.weight === "accessory" && minutes === 45;

/** The slot a row gives at the athlete's choices, or null (modelRowAt). */
export const modelRowSlot = (row: ModelRow, choices: ModelChoices, types: readonly RoundType[]): SlotDraft | null => {
  const at = modelSlotAt(row, choices.level, choices.minutes, choices.focus);
  return at ? slotOf(row, at, choices, types, tierSetFewer(at, choices.minutes)) : null;
};

/** What the fit took out of one row: `setsRemoved` sets of the full scale,
 *  all of them when `wholeRow`. */
export type ModelCut = { rowId: string; setsRemoved: number; wholeRow: boolean };

/** #413: one day after the fit. `rowIds`: the rows it keeps, in order.
 *  `cut`: what the tier and the fit took out, against the full scale. */
export type ModelDayFit = { dayId: string; rowIds: string[]; seconds: number; overBudget: boolean; cut: ModelCut[] };

/** The day's rows there at `choices`, each with its slot; `tier`: with the
 *  tier's set cut (tierSetFewer), else at the full scale. */
const daySlots = (day: ModelDay, choices: ModelChoices, types: readonly RoundType[], tier: boolean) =>
  day.rows.flatMap((row) => {
    const at = modelSlotAt(row, choices.level, choices.minutes, choices.focus);
    return at ? [{ row, at, slot: slotOf(row, at, choices, types, tier && tierSetFewer(at, choices.minutes)) }] : [];
  });

/** #413: a day's slots at the athlete's choices, fitted to the minutes. The
 *  tier comes first (modelRowSlot); a day still over minutes * 60 then loses
 *  one set on each accessory (when the tier left them all), then accessory
 *  rows, the last first, until it fits. Main rows never go, nor the
 *  day's last row. The fullest tier only measures, so «every exercise of
 *  the model» stays true. A pinned row (ModelChoices.pinned) skips both
 *  cuts; the tier's set cut still applies to it.
 *  More time never brings back a row the fit took out, only the tier's. */
const fitDay = (
  day: ModelDay,
  choices: ModelChoices,
  types: readonly RoundType[],
): { slots: SlotDraft[]; fit: ModelDayFit } => {
  const here = daySlots(day, choices, types, true).map((h) => ({
    ...h,
    full: tierSetFewer(h.at, choices.minutes) ? slotOf(h.row, h.at, choices, types, false).setRows.length : h.slot.setRows.length,
  }));
  const gone: ModelCut[] = [];
  const budget = choices.minutes * 60;
  const bySlug = choices.exercises ?? new Map<string, Exercise>();
  const secondsOf = () => draftDaySeconds(here.map((h) => h.slot), types.length, bySlug);
  const done = () => {
    const seconds = secondsOf();
    const trimmed = here.flatMap((h) => {
      const setsRemoved = h.full - h.slot.setRows.length;
      return setsRemoved > 0 ? [{ rowId: h.row.id, setsRemoved, wholeRow: false }] : [];
    });
    const cut = [...trimmed, ...gone];
    return { slots: here.map((h) => h.slot), fit: { dayId: day.id, rowIds: here.map((h) => h.row.id), seconds, overBudget: seconds > budget, cut } };
  };
  if (choices.minutes === SESSION_MINUTES[SESSION_MINUTES.length - 1] || secondsOf() <= budget) return done();
  // The tier's set cut is the fit's first step: at 45 it is already taken.
  const cuttable = (h: (typeof here)[number]) => h.at.weight === "accessory" && !choices.pinned?.has(h.row.id);
  for (const h of here) {
    if (cuttable(h) && !tierSetFewer(h.at, choices.minutes)) h.slot = slotOf(h.row, h.at, choices, types, true);
  }
  // A day never ends empty: the server refuses one.
  for (let i = here.length - 1; i >= 0 && here.length > 1 && secondsOf() > budget; i -= 1) {
    if (!cuttable(here[i]!)) continue;
    const [h] = here.splice(i, 1);
    gone.push({ rowId: h!.row.id, setsRemoved: h!.full, wholeRow: true });
  }
  return done();
};

/** The least a model takes to do whole: its longest day, in minutes rounded
 *  up, with every main and accessory set at the full scale (an optional row
 *  does not count). `exercises`: the catalog, as ModelChoices.exercises. */
export const modelMinMinutes = (
  model: Pick<ProgramModel, "roundPattern" | "days" | "daysBySessions">,
  level: ModelLevel,
  sessionsPerWeek: number,
  focus: ProgramFocus | undefined,
  exercises: ReadonlyMap<string, Exercise>,
): number => {
  const types = model.roundPattern;
  // The most minutes that still leave optional rows out.
  const minutes = SESSION_MINUTES.filter((m) => m < OPTIONAL_ROW_MIN_MINUTES).at(-1);
  if (minutes === undefined) throw new Error("SESSION_MINUTES has no value under OPTIONAL_ROW_MIN_MINUTES");
  const choices: ModelChoices = { name: "", level, focus, minutes, sessionsPerWeek, blocks: 1, effortScale: "rpe" };
  let most = 0;
  for (const day of modelDaysFor(model, sessionsPerWeek)) {
    const slots = daySlots(day, choices, types, false).map((h) => h.slot);
    most = Math.max(most, draftDaySeconds(slots, types.length, exercises));
  }
  return Math.ceil(most / 60);
};

/** A new program from `model` at the athlete's choices, each day that runs
 *  at the sessions a week (modelDaysFor) fitted to the minutes (fitDay), and
 *  how each day came out. Day names in the edit language, else English. */
export const fitModelDraft = (
  model: Omit<ProgramModel, "id">,
  choices: ModelChoices,
): { draft: Draft; days: ModelDayFit[] } => {
  const types = model.roundPattern;
  const lang = choices.editLocale ?? "en";
  const days = modelDaysFor(model, choices.sessionsPerWeek);
  const fitted = days.map((day) => fitDay(day, choices, types));
  let draft: Draft = {
    name: choices.name,
    description: "",
    ...(choices.editLocale !== undefined ? { editLocale: choices.editLocale } : {}),
    ...(choices.editLocale ? { declaredAuthorLocale: choices.editLocale } : {}),
    status: NEW_PROGRAM_STATUS,
    cycleWeeks: types.length,
    sessionsPerWeek: choices.sessionsPerWeek,
    roundTypes: [...types],
    ...(choices.focus ? { focus: choices.focus } : {}),
    ...(choices.modelId
      ? {
          origin: {
            modelId: choices.modelId,
            level: choices.level,
            minutes: choices.minutes,
            picks: { ...choices.exercisePicks },
          },
        }
      : {}),
    sessions: days.map((day, d) => ({
      id: uuidv4(),
      name: modelDayName(day, choices.focus, lang),
      notes: "",
      slots: fitted[d]!.slots,
    })),
    assignedTo: [],
    assigneeEmails: [],
  };
  for (let b = 1; b < choices.blocks; b += 1) {
    const next = addNextBlock(draft, { rounds: types.length, types });
    if (!next.ok) break;
    draft = next.draft;
  }
  return { draft, days: fitted.map((f) => f.fit) };
};

/** fitModelDraft's program. */
export const buildModelDraft = (model: Omit<ProgramModel, "id">, choices: ModelChoices): Draft =>
  fitModelDraft(model, choices).draft;
